import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Mail, MapPin, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

export default function ContactSection() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.submitContact(form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    } catch { setStatus("error"); setTimeout(() => setStatus("idle"), 3000); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
          <p className="label-mono mb-4">{t("contact_label")}</p>
          <h2 data-testid="contact-heading" className="font-['Unbounded'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">{t("contact_heading")}</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="glass-card rounded-3xl p-8 sm:p-10" data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm text-[#A0A0AB] mb-2 font-medium">{t("contact_name")}</label>
                  <input data-testid="contact-name-input" type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 focus:border-[#00F0FF]/50 transition-all" placeholder={t("contact_name_ph")} />
                </div>
                <div>
                  <label className="block text-sm text-[#A0A0AB] mb-2 font-medium">{t("contact_email")}</label>
                  <input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 focus:border-[#00F0FF]/50 transition-all" placeholder={t("contact_email_ph")} />
                </div>
                <div>
                  <label className="block text-sm text-[#A0A0AB] mb-2 font-medium">{t("contact_message")}</label>
                  <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 focus:border-[#00F0FF]/50 transition-all resize-none" placeholder={t("contact_message_ph")} />
                </div>
                <button data-testid="contact-submit-button" type="submit" disabled={status === "sending"} className="neon-btn flex items-center gap-3 text-base px-8 py-4 disabled:opacity-50">
                  {status === "sending" ? t("contact_sending") : status === "sent" ? (<><CheckCircle className="w-5 h-5" /> {t("contact_sent")}</>) : (<><Send className="w-5 h-5" /> {t("contact_send")}</>)}
                </button>
                {status === "error" && <p className="text-red-400 text-sm">{t("contact_error")}</p>}
              </div>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-5 flex flex-col gap-8">
            <div className="glass-card rounded-3xl p-8">
              <h3 className="font-['Unbounded'] text-lg font-semibold text-white mb-6">{t("contact_connect")}</h3>
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center shrink-0"><Globe className="w-5 h-5 text-[#00F0FF]" /></div>
                  <div><p className="text-xs text-[#A0A0AB] mb-1">{t("contact_website")}</p><p className="text-white text-sm font-medium">fortisdigitalllc.com</p></div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#00F0FF]" /></div>
                  <div><p className="text-xs text-[#A0A0AB] mb-1">{t("contact_email")}</p><p className="text-white text-sm font-medium">info@fortisdigitalllc.com</p></div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#00F0FF]" /></div>
                  <div><p className="text-xs text-[#A0A0AB] mb-1">{t("contact_location")}</p><p className="text-white text-sm font-medium">{t("contact_location_val")}</p></div>
                </div>
              </div>
            </div>
            <div className="glass-card rounded-3xl p-8">
              <h3 className="font-['Unbounded'] text-lg font-semibold text-white mb-4">{t("contact_follow")}</h3>
              <p className="text-[#A0A0AB] text-sm mb-6">{t("contact_follow_text")}</p>
              <div className="flex gap-3">
                {["X", "IG", "LI", "YT"].map((platform) => (
                  <div key={platform} data-testid={`social-${platform.toLowerCase()}`}
                    className="w-10 h-10 rounded-xl border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-[#00F0FF] hover:border-[#00F0FF]/30 transition-all cursor-pointer text-xs font-['JetBrains_Mono'] font-bold">
                    {platform}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
